import { useState, useCallback, useEffect } from 'react'
import { useAtomValue } from 'jotai'
import { ArrowUp, Paperclip, Square } from 'lucide-react'
import { toast } from 'sonner'
import { currentClassIdAtom, currentClassroomAtom, currentMessagesAtom, currentSessionIdAtom, isStreamingAtom } from '../state/store'
import { useAutoResizeTextarea } from '../hooks/useAutoResizeTextarea'
import { ATTACHMENT_ACCEPT, useAttachmentUpload } from '../hooks/useAttachmentUpload'
import { Tooltip, TooltipTrigger, TooltipContent, cn } from '@neo-agents/ui'
import type { ClassroomMaterial } from '../api/backend'
import { useWorkspace } from '../contexts/WorkspaceContext'
import type { ChatAttachment } from '../state/types'
import { buildStorageFileUrl, generateId } from '../state/types'
import { AttachmentList } from './AttachmentList'
import { ClassMaterialsDropdown } from './ClassMaterialsDropdown'

interface ChatInputActions {
  onSend?: (content: string, attachments: ChatAttachment[]) => void
  onStop?: () => void
}

export const chatInputActionsRef: { current: ChatInputActions } = { current: {} }

interface ChatInputProps {
  placeholder?: string
}

export function ChatInput({ placeholder }: ChatInputProps) {
  const [value, setValue] = useState('')
  const isStreaming = useAtomValue(isStreamingAtom)
  const currentClassId = useAtomValue(currentClassIdAtom)
  const currentSessionId = useAtomValue(currentSessionIdAtom)
  const currentMessages = useAtomValue(currentMessagesAtom)
  const currentClassroom = useAtomValue(currentClassroomAtom)
  const { memberRole } = useWorkspace()
  const { textareaRef, resize } = useAutoResizeTextarea()
  const { attachments, setAttachments, addFiles, removeAttachment, clearAttachments, isUploadingAttachments } = useAttachmentUpload()
  const hasIncompleteAttachments = attachments.some((attachment) => attachment.uploadStatus !== 'uploaded')
  const canSend = (value.trim().length > 0 || attachments.length > 0) && !isStreaming && !hasIncompleteAttachments

  const handleSend = useCallback(() => {
    if (!canSend) return
    chatInputActionsRef.current.onSend?.(value.trim(), attachments)
    setValue('')
    clearAttachments()
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [canSend, value, attachments, clearAttachments, textareaRef])

  const handleStop = useCallback(() => {
    chatInputActionsRef.current.onStop?.()
  }, [])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (isStreaming) return
        handleSend()
      }
    },
    [handleSend, isStreaming],
  )

  const handleFileUpload = useCallback(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = true
    input.accept = ATTACHMENT_ACCEPT
    input.onchange = async () => {
      if (!input.files) return
      await addFiles(Array.from(input.files))
    }
    input.click()
  }, [addFiles])

  const handleMaterialSelect = useCallback((material: ClassroomMaterial) => {
    if (!material.attachment) {
      toast.error('This material does not have an attachment yet.')
      return
    }

    const attachment = material.attachment
    const nextAttachment: ChatAttachment = {
      id: generateId(),
      name: attachment.filename,
      type: attachment.mimeType,
      size: attachment.sizeBytes ?? 0,
      url: buildStorageFileUrl(attachment.storageName || attachment.storageId),
      storageId: attachment.storageId,
      storageName: attachment.storageName,
      uploadStatus: 'uploaded',
      uploadProgress: 100,
      encoding: attachment.mimeType.startsWith('image/') ? 'base64' : 'text',
    }

    setAttachments((prev) =>
      prev.some((item) => item.storageId === attachment.storageId)
        ? prev
        : [...prev, nextAttachment],
    )
    textareaRef.current?.focus()
  }, [setAttachments, textareaRef])

  const isClassHomeState = Boolean(currentClassId) && currentMessages.length === 0
  const isGeneralChatEmptyState = Boolean(currentSessionId) && !currentClassId && currentMessages.length === 0
  const isEndedClassChat = Boolean(currentClassId && currentClassroom?.status === 'ENDED')
  const isAdminClassChat = Boolean(currentClassId && memberRole === 'ADMIN')
  const shouldResetDraft = !currentSessionId || isClassHomeState || isGeneralChatEmptyState

  useEffect(() => {
    if (!shouldResetDraft) return
    setValue('')
    clearAttachments()
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [clearAttachments, shouldResetDraft, textareaRef])

  if (shouldResetDraft) {
    return null
  }

  if (isEndedClassChat || isAdminClassChat) {
    return null
  }

  return (
    <div className="shrink-0 px-4 pb-4 pt-2">
      <div className="max-w-[840px] mx-auto">
        {/* Input card — neobrutalism style */}
        <div className="rounded-base border-2 border-nb-border bg-secondary-bg nb-shadow">
          <AttachmentList attachments={attachments} onRemove={removeAttachment} />

          {/* Textarea area */}
          <div className="px-4 pt-3 pb-2">
            <textarea
              ref={textareaRef}
              className="w-full resize-none bg-transparent text-base text-foreground placeholder:text-foreground/40 outline-none min-h-[48px] max-h-[200px] font-base"
              placeholder={currentClassId ? 'Ask me about what you\'re learning!' : (placeholder ?? 'Ask me anything...')}
              rows={2}
              value={value}
              onChange={(e) => {
                setValue(e.target.value)
                resize()
              }}
              onKeyDown={handleKeyDown}
            />
          </div>

          {/* Bottom toolbar */}
          <div className="flex items-center justify-between px-3 py-2 border-t-2 border-nb-border">
            <div className="flex items-center gap-2">
              {currentClassId && currentClassroom ? (
                <ClassMaterialsDropdown
                  materials={currentClassroom.materials ?? []}
                  onSelect={handleMaterialSelect}
                />
              ) : null}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="rounded-base border-2 border-nb-border bg-bg p-1.5 text-foreground transition-colors hover:bg-main hover:text-main-foreground"
                    onClick={handleFileUpload}
                    type="button"
                  >
                    <Paperclip className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Upload attachment</TooltipContent>
              </Tooltip>
            </div>

            {/* Right: send/stop */}
            <div className="flex items-center gap-2">
              {isStreaming ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-base border-2 border-nb-border p-1.5 bg-destructive text-white font-heading hover:translate-x-boxShadowX hover:translate-y-boxShadowY transition-all nb-shadow-sm nb-hover-press"
                      onClick={handleStop}
                    >
                      <Square className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Stop generating</TooltipContent>
                </Tooltip>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        'rounded-base border-2 border-nb-border p-1.5 font-heading transition-all',
                        canSend
                          ? 'bg-main text-main-foreground nb-shadow-sm nb-hover-press'
                          : 'bg-foreground/10 text-foreground/30 cursor-not-allowed',
                      )}
                      onClick={handleSend}
                      disabled={!canSend}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isUploadingAttachments
                      ? 'Wait for attachments to finish uploading'
                      : hasIncompleteAttachments
                        ? 'Remove failed attachments before sending'
                        : 'Send message'}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
